import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';

// How long a piece of visitor-generated demo data survives before the
// rolling cleanup removes it. A rolling window (checked every few minutes)
// rather than a synchronized full wipe, so one visitor's cart/order/account
// never disappears out from under another visitor mid-session — only data
// that's genuinely gone stale gets swept.
const RETENTION_MINUTES = Number(process.env.DEMO_RETENTION_MINUTES ?? 30);

@Injectable()
export class DemoCleanupService {
  private readonly logger = new Logger(DemoCleanupService.name);

  constructor(private readonly prisma: PrismaService) {}

  @Cron('*/5 * * * *')
  async cleanup(): Promise<void> {
    if (process.env.DEMO_MODE !== 'true') return;

    const cutoff = new Date(Date.now() - RETENTION_MINUTES * 60_000);

    const [carts, checkouts, orders, users, reviews, customers] = await Promise.all([
      this.cleanupCarts(cutoff),
      this.cleanupCheckouts(cutoff),
      this.cleanupOrders(cutoff),
      this.cleanupDemoUsers(cutoff),
      this.cleanupReviews(cutoff),
      this.cleanupCrmCustomers(cutoff),
    ]);

    const total = carts + checkouts + orders + users + reviews + customers;
    if (total > 0) {
      this.logger.log(`Removed ${total} stale demo record(s) older than ${RETENTION_MINUTES}m`);
    }
  }

  private async cleanupCarts(cutoff: Date): Promise<number> {
    const stale = await this.prisma.cart.findMany({
      where: { createdAt: { lt: cutoff } },
      select: { id: true },
    });
    if (!stale.length) return 0;

    const ids = stale.map((c) => c.id);
    await this.prisma.cartItem.deleteMany({ where: { cartId: { in: ids } } });
    await this.prisma.cart.deleteMany({ where: { id: { in: ids } } });
    return ids.length;
  }

  private async cleanupCheckouts(cutoff: Date): Promise<number> {
    const stale = await this.prisma.checkoutSession.findMany({
      where: { createdAt: { lt: cutoff } },
      select: { id: true },
    });
    if (!stale.length) return 0;

    const ids = stale.map((c) => c.id);
    await this.prisma.checkoutLineItem.deleteMany({ where: { checkoutId: { in: ids } } });
    await this.prisma.checkoutSession.deleteMany({ where: { id: { in: ids } } });
    return ids.length;
  }

  // Orders and their downstream records (payments, refunds, shipments) have
  // no DB-enforced FK cascade in this schema, so children are removed before
  // the order itself, keyed off the order's own age rather than each child's
  // — a payment/refund created moments after an old order shouldn't survive
  // as an orphan just because it's individually "younger" than the cutoff.
  private async cleanupOrders(cutoff: Date): Promise<number> {
    const stale = await this.prisma.order.findMany({
      where: { createdAt: { lt: cutoff } },
      select: { id: true },
    });
    if (!stale.length) return 0;

    const orderIds = stale.map((o) => o.id);

    const payments = await this.prisma.payment.findMany({
      where: { orderId: { in: orderIds } },
      select: { id: true },
    });
    if (payments.length) {
      await this.prisma.paymentTransaction.deleteMany({
        where: { paymentId: { in: payments.map((p) => p.id) } },
      });
    }

    const refunds = await this.prisma.refund.findMany({
      where: { orderId: { in: orderIds } },
      select: { id: true },
    });
    if (refunds.length) {
      await this.prisma.refundItem.deleteMany({
        where: { refundId: { in: refunds.map((r) => r.id) } },
      });
    }

    await Promise.all([
      this.prisma.orderItem.deleteMany({ where: { orderId: { in: orderIds } } }),
      this.prisma.orderStatusHistory.deleteMany({ where: { orderId: { in: orderIds } } }),
      this.prisma.shipment.deleteMany({ where: { orderId: { in: orderIds } } }),
      this.prisma.payment.deleteMany({ where: { orderId: { in: orderIds } } }),
      this.prisma.refund.deleteMany({ where: { orderId: { in: orderIds } } }),
    ]);

    await this.prisma.order.deleteMany({ where: { id: { in: orderIds } } });
    return orderIds.length;
  }

  // Scoped to role: 'customer' so the seeded admin (role: 'admin') and any
  // real staff accounts are never touched. Deleting the user cascades its
  // addresses and wishlist items at the DB level (onDelete: Cascade).
  private async cleanupDemoUsers(cutoff: Date): Promise<number> {
    const result = await this.prisma.user.deleteMany({
      where: { role: 'customer', createdAt: { lt: cutoff } },
    });
    return result.count;
  }

  // Seed data never creates reviews, so any row here is visitor-generated.
  // ReviewPhoto cascades at the DB level (onDelete: Cascade on the review FK).
  private async cleanupReviews(cutoff: Date): Promise<number> {
    const result = await this.prisma.review.deleteMany({ where: { createdAt: { lt: cutoff } } });
    return result.count;
  }

  private async cleanupCrmCustomers(cutoff: Date): Promise<number> {
    const stale = await this.prisma.customer.findMany({
      where: { createdAt: { lt: cutoff } },
      select: { id: true },
    });
    if (!stale.length) return 0;

    const ids = stale.map((c) => c.id);
    await Promise.all([
      this.prisma.customerAddress.deleteMany({ where: { customerId: { in: ids } } }),
      this.prisma.customerSession.deleteMany({ where: { customerId: { in: ids } } }),
      this.prisma.customerTag.deleteMany({ where: { customerId: { in: ids } } }),
      this.prisma.customerWishlistItem.deleteMany({ where: { customerId: { in: ids } } }),
    ]);
    await this.prisma.customer.deleteMany({ where: { id: { in: ids } } });
    return ids.length;
  }
}
